/* eslint-disable no-unused-vars */
import { TagHandlerBase } from './tagHandlerBase';
import store from '@/store';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
const THREE = window.THREE;

/**
 * Handler for structural tags
 */
export class StructuralHandler extends TagHandlerBase {
    /**
     * Check if tag type should be processed
     * @param {string} type - Tag type
     * @param {Object} displaySettings - Display settings
     * @returns {boolean} Should process structural
     */
    shouldProcess(type, displaySettings) {
        return (displaySettings.showManualStructural && type === 'manual_structural') || 
               (displaySettings.showStructural && this.isViewer2d() && type === 'structural');
    }
    
    /**
     * Collect structural data
     * @param {Object} settings - Display settings
     * @param {Object} viewerData - Viewer position data
     * @returns {Object} Collected structural data
     */
    collectData(settings, viewerData) {
        const { isGlobalSearchFilter, displaySettings } = settings;
        const data = {};
        
        // Clear any existing cluster text meshes
        this.clearClusterTextMeshes();
        
        // Get structural type from extension
        let type = '';
        if (this.extension.currentTagType) {
            type = this.extension.currentTagType;
        } else {
            // Try to determine type based on available information
            if (displaySettings.showManualStructural) {
                type = 'manual_structural';
            } else if (displaySettings.showStructural && this.isViewer2d()) {
                type = 'structural';
            } else {
                return data; // No applicable type
            }
        }
        
        // Skip if not applicable
        if (!this.shouldProcess(type, displaySettings)) return data;
        
        let structurals = this.getStructuralsByType(type);

        if (isGlobalSearchFilter) {
            let isolatedTags = store.getters['GET_ISOLATED_IDS'];    
            if (isolatedTags.size > 0) {
                structurals = structurals.filter(t => isolatedTags.has(t.id));
            }
        }
        
        // Filter structurals before clustering
        structurals = structurals.filter(structural => {
            // Comment out global search filtering for now
            /*
            // Skip if filtered out by global search
            if (this.shouldSkipStructural(structural, type, isGlobalSearchFilter)) return false;
            */
            
            return true;
        });
        
        // Only apply clustering after filtering in 2D view
        if (this.isViewer2d() && structurals.length > 0) {
            // Apply clustering logic
            const { items, clusters } = this.getTagsClustersAndItems2D(structurals, viewerData.position);
            
            // Process clusters
            this.processClusters(clusters, data, 'structural-cluster');
            
            // Update to use filtered items
            structurals = items;
        }
        
        structurals.forEach(structural => {
            // Get point in viewer space
            let point = this.getPointForStructural(structural, type);
            if (!point) return;
            
            // Create point cloud data
            const pcData = this.createStructuralData(structural, point);
            
            // Add to category collection
            const category = structural.category ?? 1;
            data[category] ? data[category].push(pcData) : data[category] = [pcData];
        });
        
        return data;
    }
    
    /**
     * Get structurals for specific type
     * @param {string} type - Structural type
     * @returns {Array} Structural items
     */
    getStructuralsByType(type) {
        if (type === 'manual_structural') {
            // Manual structural
            const tags = store.state.forgeService.getManualStructuralTagsInLevel(store.state.selectedLevel);
            return this.isViewer2d() ? tags : store.getters.GET_MANUAL_STRUCTURAL_TAGS;
        } else if (type === 'structural') {
            // Auto structural
            return store.state.forgeService.getStructuralTagsInLevel(store.state.selectedLevel);
        }
        return [];
    }
    
    /**
     * Check if structural should be skipped based on filters
     * @param {Object} structural - Structural object
     * @param {string} type - Tag type
     * @param {boolean} isGlobalSearchFilter - Global search filter flag
     * @returns {boolean} Should skip structural
     */
    shouldSkipStructural(structural, type, isGlobalSearchFilter) {
        if (type === 'manual_structural') {
            return isGlobalSearchFilter && !store.state.filteredTags.includes(structural);
        } else if (type === 'structural') {
            const filteredTagsIds = store.getters.GET_STRUCTURAL_TAGS.map(t => t.id);
            return isGlobalSearchFilter && !filteredTagsIds.some(id => id == structural.id);
        }
        return false;
    }
    
    /**
     * Get point in viewer space for structural
     * @param {Object} structural - Structural object
     * @param {string} type - Tag type
     * @returns {Object|null} Point in viewer space
     */
    getPointForStructural(structural, type) {
        if (!structural) {
            debugWarn('structuralHandler', 'Structural is null or undefined');
            return null;
        }

        let point;
        
        if (type === 'structural') {
            structural.point = Object.assign(new THREE.Vector3(), structural.point);
            point = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(structural.point);
            point.x += .001; // Offset for visibility
        } else {
            point = this.isViewer2d() 
                ? store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(structural.point) 
                : structural.getPointInView();
                
            // Skip if outside crop box in 3D view
            if (!this.isViewer2d() && !store.state.forgeService.getMainExtension()?.isWithinCropBox(point)) {
                return null;
            }
            
            if (this.isViewer2d()) point.z = .1;
        }
        
        return point;
    }
    
    /**
     * Create structural data for point cloud
     * @param {Object} structural - Structural object
     * @param {Object} point - Point in viewer space
     * @returns {Object} Point cloud data
     */
    createStructuralData(structural, point) {
        const _this = this;
        return {
            name: structural.name,
            thumbnailURL: structural.thumbnailUrl,
            object: structural,
            point: point,
            onClick: (event) => {
                store.dispatch('FETCH_STRUCTURAL_TAG_BY_ID', structural.id).then(tag => {
                    store.state.forgeService.onClickOn3dIcon(tag, event, _this.extension.viewType);
                });
            }
        };
    }
}
